import React, { useState, useRef, useEffect } from 'react';
import { SAMPLE_REAL_WORLD_SIGNS } from '../../core/curriculum/curriculumData';
import { tokenizeSentence, TokenizedSentence, syllabifyWord } from '../../core/reading/syllabifier';
import { speechProvider } from '../../core/speech/speechProvider';
import { appRepository, DiscoveredWord } from '../../core/storage/repository';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import { BackIcon, BookIcon, CameraIcon, SpeakerIcon, UploadIcon } from '../../components/icons/ReadingIcons';
import confetti from 'canvas-confetti';
import { browserCameraProvider } from '../../adapters/camera/browserCameraProvider';
import { tesseractOCRProvider } from '../../adapters/ocr/tesseractOCRProvider';

interface CameraReaderViewProps {
  onBack: () => void;
  onGoToMyWords?: () => void;
}

export const CameraReaderView: React.FC<CameraReaderViewProps> = ({ onBack, onGoToMyWords }) => {
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [recognizedText, setRecognizedText] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [manualText, setManualText] = useState('');
  const [tokenizedDoc, setTokenizedDoc] = useState<TokenizedSentence | null>(null);
  const [savedSuccessMsg, setSavedSuccessMsg] = useState<string | null>(null);
  const [mascotState, setMascotState] = useState<'idle' | 'pointing' | 'reading' | 'happy' | 'celebrate'>('pointing');
  const [mascotMessage, setMascotMessage] = useState('Arahkan kamera ke tulisan di sekitarmu, lalu ambil foto!');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Initialize camera on mount if requested
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (!cameraActive || !streamRef.current || !videoRef.current) return;

    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(error => {
      console.warn('Camera preview play error:', error);
      setCameraError('Pratinjau kamera belum bisa diputar. Coba izinkan kamera lalu tekan Buka Kamera lagi.');
    });
  }, [cameraActive]);

  const startCamera = async () => {
    setCameraError(null);
    setCapturedImage(null);
    setRecognizedText(null);
    setTokenizedDoc(null);
    setOcrError(null);
    setOcrProgress(0);
    setSavedSuccessMsg(null);

    try {
      const stream = await browserCameraProvider.start({
        facingMode: 'environment',
        width: 1280,
        height: 720,
      }) as MediaStream;

      streamRef.current = stream;
      setCameraActive(true);
      setMascotState('pointing');
      setMascotMessage('Arahkan ke tulisan yang jelas, lalu tekan tombol foto!');
    } catch (err: unknown) {
      console.warn('Camera start error:', err);
      setCameraError('Kamera belum boleh digunakan atau tidak tersedia. Kamu bisa memilih contoh foto atau mengunggah gambar!');
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      browserCameraProvider.stop(streamRef.current);
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

    stopCamera();
    setCapturedImage(dataUrl);
    processImageForReading(dataUrl);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setCapturedImage(dataUrl);
      processImageForReading(dataUrl);
    };
    e.target.value = '';
    reader.readAsDataURL(file);
  };

  const handleSelectSample = (sample: typeof SAMPLE_REAL_WORLD_SIGNS[0]) => {
    setCapturedImage(sample.imageUrl);
    processImageForReading(sample.imageUrl, sample.text);
  };

  const handleManualEntry = () => {
    const text = manualText.trim();
    if (!text) return;
    setCapturedImage(null);
    setRecognizedText(text);
    setEditText(text);
    setTokenizedDoc(tokenizeSentence(text));
    setIsManualEntry(false);
    setMascotState('happy');
    setMascotMessage('Tulisan siap dibaca! Sentuh kata atau suku kata untuk mendengar suaranya.');
  };

  const processImageForReading = async (imageUrl: string, sampleText?: string) => {
    setIsProcessing(true);
    setOcrError(null);
    setOcrProgress(sampleText ? 100 : 0);
    setMascotState('reading');
    setMascotMessage('Sebentar ya, Rara sedang membaca tulisan pada fotonya...');
    speechProvider.speak('Sebentar ya, aku sedang membaca tulisannya.', { rate: 0.9 });

    try {
      let detectedText = sampleText;

      if (!detectedText) {
        const result = await tesseractOCRProvider.recognize(imageUrl, progress => {
          setOcrProgress(progress.progress);
        });
        detectedText = result.text;
      }

      if (!detectedText) {
        throw new Error('Tidak ada tulisan yang terbaca');
      }

      setRecognizedText(detectedText);
      setEditText(detectedText);
      setTokenizedDoc(tokenizeSentence(detectedText));
      setMascotState('happy');
      setMascotMessage('Wah, Rara menemukan tulisannya! Sentuh kata atau suku kata untuk mendengar suaranya.');
    } catch (error) {
      console.error('OCR processing error:', error);
      setOcrError('Rara belum bisa membaca tulisan di foto ini. Coba foto lebih dekat, lebih terang, atau koreksi teks secara manual.');
      setMascotState('pointing');
      setMascotMessage('Tulisan di foto belum terbaca. Yuk coba foto yang lebih jelas!');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveWord = (wordText: string, syllables: string[]) => {
    const newWord: DiscoveredWord = {
      id: `disc-${Date.now()}`,
      originalText: wordText.toUpperCase(),
      normalizedText: wordText.toLowerCase(),
      syllables: syllables.map(s => s.toUpperCase()),
      confidence: 'high',
      source: 'camera',
      discoveredAt: new Date().toISOString(),
      mastery: 'baru',
      imageUrl: capturedImage || undefined,
    };

    const added = appRepository.addDiscoveredWord(newWord);
    if (added) {
      setSavedSuccessMsg(`Kata "${wordText.toUpperCase()}" berhasil disimpan ke Kataku! ⭐ +1 Bintang`);
      setMascotState('celebrate');
      setMascotMessage(`Yeay! Kata "${wordText.toUpperCase()}" masuk ke koleksi Kataku!`);
      confetti({ particleCount: 40, spread: 60, origin: { y: 0.7 } });
      setTimeout(() => setSavedSuccessMsg(null), 4000);
    } else {
      setSavedSuccessMsg(`Kata "${wordText.toUpperCase()}" sudah ada di Kataku.`);
      setTimeout(() => setSavedSuccessMsg(null), 3000);
    }
  };

  const handleUpdateText = () => {
    if (!editText.trim()) return;
    setRecognizedText(editText.trim());
    const tokenized = tokenizeSentence(editText.trim());
    setTokenizedDoc(tokenized);
    setIsEditing(false);
    setMascotState('happy');
    setMascotMessage('Tulisan berhasil diperbaiki!');
  };

  const handleSpeakSyllable = (syllable: string) => {
    speechProvider.playChime('tap');
    speechProvider.speak(syllable.toLowerCase(), { rate: 0.8, pitch: 1.1 });
  };

  const handleSpeakWord = (word: string, syllables: string[]) => {
    speechProvider.playChime('tap');
    speechProvider.speakSyllablesAndWord(syllables.map(s => s.toLowerCase()), word);
  };

  const handleSpeakSentence = () => {
    if (recognizedText) {
      speechProvider.playChime('tap');
      speechProvider.speak(recognizedText, { rate: 0.85 });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFDF8] pb-12">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-amber-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => {
            stopCamera();
            onBack();
          }}
          className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-900 font-bold px-4 py-2.5 rounded-2xl transition"
        >
          <BackIcon className="w-6 h-6" />
          <span className="text-sm md:text-base">Kembali</span>
        </button>

        <h1 className="text-xl md:text-2xl font-display font-bold text-[#FFB84D] flex items-center gap-2">
          <CameraIcon className="w-9 h-9" /> Ayo Baca Kamera
        </h1>

        {onGoToMyWords && (
          <button
            onClick={onGoToMyWords}
            className="flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 active:scale-95 text-white font-bold text-xs md:text-sm px-3.5 py-2 rounded-2xl shadow"
          >
            <BookIcon className="w-6 h-6" /> Kataku
          </button>
        )}
      </div>

      <div className="max-w-3xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-6">
        {/* Mascot companion */}
        <RaraMascot
          state={mascotState}
          message={mascotMessage}
          size="sm"
        />

        {/* Success toast notification */}
        {savedSuccessMsg && (
          <div className="bg-emerald-500 text-white font-bold px-4 py-3 rounded-2xl text-center shadow-lg border-2 border-emerald-600 animate-bounce-short">
            {savedSuccessMsg}
          </div>
        )}

        {/* Camera Stage / Viewport */}
        {!capturedImage && !cameraActive && !recognizedText && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border-4 border-amber-200 flex flex-col items-center text-center">
            <div className="w-24 h-24 md:w-32 md:h-32 bg-amber-50 rounded-full border-4 border-amber-300 flex items-center justify-center text-5xl md:text-6xl mb-4">
              <CameraIcon className="w-16 h-16" />
            </div>
            <h2 className="text-2xl md:text-3xl font-display font-black text-[#243047]">
              Foto Tulisan di Sekitarmu!
            </h2>
            <p className="text-sm md:text-base text-gray-600 mt-2 max-w-md">
              Arahkan kamera ke rambu jalan, buku cerita, kemasan, atau papan nama. Rara akan membacanya untukmu!
            </p>

            {cameraError && (
              <div className="mt-4 p-3 bg-amber-50 border-2 border-amber-300 rounded-2xl text-xs md:text-sm text-amber-900 font-medium max-w-md">
                {cameraError}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-4 mt-6 w-full max-w-md">
              <button
                onClick={startCamera}
                className="w-full py-4 bg-[#FFB84D] hover:bg-amber-500 active:scale-95 text-amber-950 font-display font-black text-lg md:text-xl rounded-2xl shadow-lg flex items-center justify-center gap-3 transition"
              >
                <CameraIcon className="w-7 h-7" />
                <span>Buka Kamera Langsung</span>
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3.5 bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-700 font-bold text-sm md:text-base rounded-2xl border-2 border-gray-300 flex items-center justify-center gap-2 transition"
              >
                <UploadIcon className="w-6 h-6" />
                <span>Unggah Foto dari HP</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                aria-label="Pilih foto untuk dibaca"
                className="hidden"
              />
            </div>

            <p className="mt-4 max-w-md text-xs leading-relaxed text-gray-500">
              Privasi: kamera hanya dipakai setelah kamu menekan tombol. Foto diproses di perangkat ini
              dan tidak diunggah otomatis.
            </p>

            <button
              type="button"
              onClick={() => setIsManualEntry(value => !value)}
              className="mt-3 text-sm font-bold text-teal-700 underline underline-offset-2"
            >
              ✏️ Ketik tulisan tanpa foto
            </button>

            {isManualEntry && (
              <div className="mt-4 flex w-full max-w-md gap-2 rounded-2xl border-2 border-teal-200 bg-teal-50 p-3">
                <input
                  type="text"
                  value={manualText}
                  onChange={event => setManualText(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') handleManualEntry();
                  }}
                  placeholder="Contoh: buku cerita"
                  aria-label="Tulisan manual"
                  className="min-w-0 flex-1 rounded-xl border-2 border-teal-200 bg-white px-3 py-2 text-sm font-bold text-[#243047] focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
                <button
                  type="button"
                  onClick={handleManualEntry}
                  className="rounded-xl bg-teal-600 px-3 py-2 text-sm font-bold text-white hover:bg-teal-700"
                >
                  Baca
                </button>
              </div>
            )}

            {/* Instant Sample Photos for Testing */}
            <div className="mt-8 w-full border-t-2 border-amber-100 pt-6">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                Atau coba contoh foto rambu tulisan ini:
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SAMPLE_REAL_WORLD_SIGNS.map((sample, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectSample(sample)}
                    className="flex flex-col items-center bg-amber-50/70 hover:bg-amber-100/90 border-2 border-amber-200 rounded-2xl p-2.5 transition active:scale-95 text-left"
                  >
                    <div className="w-full h-20 rounded-xl overflow-hidden mb-2 bg-gray-200">
                      <img
                        src={sample.imageUrl}
                        alt={sample.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-xs font-bold text-[#243047] truncate w-full">{sample.title}</div>
                    <div className="text-[10px] text-amber-800 font-medium truncate w-full">"{sample.text}"</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Live Camera Feed */}
        {cameraActive && (
          <div className="bg-black rounded-3xl p-4 shadow-2xl flex flex-col items-center relative overflow-hidden">
            <div className="relative w-full max-w-lg aspect-4/3 rounded-2xl overflow-hidden bg-gray-900 border-4 border-amber-300">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />

              {/* OCR Target Frame Overlay */}
              <div className="absolute inset-8 border-3 border-dashed border-white/80 rounded-2xl pointer-events-none flex items-center justify-center">
                <div className="bg-black/50 text-white text-xs px-3 py-1 rounded-full font-bold">
                  Arahkan tulisan ke dalam kotak ini
                </div>
              </div>
            </div>

            {/* Camera Controls */}
            <div className="flex items-center justify-center gap-6 mt-6">
              <button
                onClick={stopCamera}
                className="px-5 py-3 rounded-2xl bg-gray-700 text-white font-bold text-sm hover:bg-gray-600 active:scale-95"
              >
                Batal
              </button>

              <button
                onClick={capturePhoto}
                className="w-20 h-20 bg-amber-400 border-4 border-white rounded-full flex items-center justify-center text-3xl shadow-xl active:scale-90 hover:bg-amber-300 transition"
                title="Ambil Foto"
                aria-label="Ambil foto tulisan"
              >
                📸
              </button>
            </div>
          </div>
        )}

        {/* Processing State */}
        {isProcessing && (
          <div className="bg-white rounded-3xl p-8 shadow-xl border-4 border-amber-200 flex flex-col items-center text-center">
            <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mb-4" />
            <h3 className="text-xl font-display font-bold text-[#243047]">
              Membaca Tulisan...
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Rara sedang mengenali tulisan pada foto ({ocrProgress}%).
            </p>
          </div>
        )}

        {capturedImage && !isProcessing && ocrError && (
          <div className="bg-amber-50 rounded-3xl p-6 shadow-xl border-4 border-amber-200 text-center">
            <div className="text-4xl mb-3">🔎</div>
            <h3 className="text-xl font-display font-bold text-amber-950">Tulisan belum terbaca</h3>
            <p className="text-sm text-amber-900 mt-2">{ocrError}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 mt-5">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-3 bg-white hover:bg-amber-100 border-2 border-amber-300 rounded-2xl text-amber-950 font-bold"
              >
                🖼️ Pilih Foto Lain
              </button>
              <button
                onClick={() => {
                  setCapturedImage(null);
                  setOcrError(null);
                  startCamera();
                }}
                className="px-4 py-3 bg-[#FFB84D] hover:bg-amber-500 rounded-2xl text-amber-950 font-bold"
              >
                📸 Coba Kamera
              </button>
            </div>
          </div>
        )}

        {/* Recognized OCR Interactive Reader */}
        {!isProcessing && recognizedText && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border-4 border-amber-300 flex flex-col gap-6">
            {/* Captured Photo thumbnail & Actions */}
            <div className="flex items-center justify-between gap-4 border-b-2 border-gray-100 pb-4">
              <div className="flex items-center gap-3">
                {capturedImage ? (
                  <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-gray-300 bg-gray-100 shrink-0">
                    <img
                      src={capturedImage}
                      alt="Foto yang dibaca"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border-2 border-teal-200 bg-teal-50 text-2xl">
                    ✏️
                  </div>
                )}
                <div>
                  <div className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full inline-block">
                    ✓ Berhasil Terbaca
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{capturedImage ? 'Tulisan dari dunia nyata' : 'Tulisan manual'}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {capturedImage && <button
                  onClick={() => {
                    setCapturedImage(null);
                    setRecognizedText(null);
                    setTokenizedDoc(null);
                    startCamera();
                  }}
                  className="px-3.5 py-2 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-900 rounded-xl font-bold text-xs"
                >
                  📸 Foto Lagi
                </button>}
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-700 rounded-xl font-bold text-xs"
                >
                  ✏️ {isEditing ? 'Batal Edit' : 'Edit Teks'}
                </button>
              </div>
            </div>

            {/* Optional Correction Editor */}
            {isEditing && (
              <div className="bg-amber-50 p-4 rounded-2xl border-2 border-amber-200">
                <label className="text-xs font-bold text-amber-900 block mb-1">
                  Koreksi Teks jika ada salah ketik dari kamera:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    className="flex-1 bg-white border-2 border-amber-300 rounded-xl px-3 py-2 text-base font-bold text-[#243047] focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                  <button
                    onClick={handleUpdateText}
                    className="px-4 py-2 bg-emerald-600 text-white font-bold rounded-xl text-sm shadow hover:bg-emerald-700"
                  >
                    Simpan
                  </button>
                </div>
              </div>
            )}

            {/* Full Sentence Display */}
            <div className="text-center py-2">
              <div className="text-2xl md:text-3xl font-display font-black text-[#243047] tracking-wide">
                "{recognizedText}"
              </div>
              <button
                onClick={handleSpeakSentence}
                className="mt-3 inline-flex items-center gap-2 bg-[#4F7CF7] hover:bg-blue-600 active:scale-95 text-white font-bold text-sm px-5 py-2.5 rounded-full shadow transition"
              >
                <SpeakerIcon className="w-5 h-5" />
                <span>Dengar Seluruh Kalimat</span>
              </button>
            </div>

            {/* Interactive Syllable Breakdown Cards */}
            <div className="bg-amber-50/60 rounded-3xl p-5 border-2 border-amber-200">
              <div className="text-xs font-bold text-amber-900 mb-3 uppercase tracking-wider text-center">
                Sentuh Suku Kata untuk Belajar Membaca:
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4">
                {tokenizedDoc?.words.map((token, idx) => {
                  if (!token.isWord) {
                    return (
                      <span key={idx} className="text-2xl font-bold text-gray-500 self-center">
                        {token.text}
                      </span>
                    );
                  }

                  return (
                    <div
                      key={idx}
                      className="bg-white rounded-2xl p-3 border-2 border-amber-300 shadow-sm flex flex-col items-center gap-2"
                    >
                      {/* Word Header */}
                      <button
                        onClick={() => handleSpeakWord(token.text, token.syllables)}
                        className="text-base font-display font-extrabold text-[#243047] hover:text-blue-600 flex items-center gap-1 active:scale-95"
                        title="Dengar Kata Utuh"
                      >
                        <span>{token.text.toUpperCase()}</span>
                        <SpeakerIcon className="w-4 h-4 text-blue-500" />
                      </button>

                      {/* Syllable Chips Breakdown */}
                      <div className="flex items-center gap-1.5">
                        {token.syllables.map((syl, sIdx) => (
                          <button
                            key={sIdx}
                            onClick={() => handleSpeakSyllable(syl)}
                            className="px-3 py-2 bg-emerald-50 hover:bg-emerald-200 border-2 border-emerald-300 text-emerald-900 font-display font-black text-lg md:text-xl rounded-xl active:scale-90 shadow-xs transition"
                            title={`Bunyikan ${syl}`}
                          >
                            {syl.toUpperCase()}
                          </button>
                        ))}
                      </div>

                      {/* Save Word to Kataku Button */}
                      <button
                        onClick={() => handleSaveWord(token.text, token.syllables)}
                        className="mt-1 text-xs text-amber-700 hover:text-amber-900 font-bold bg-amber-100 hover:bg-amber-200 px-3 py-1 rounded-full flex items-center gap-1 active:scale-95 transition"
                      >
                        <span>⭐</span>
                        <span>Simpan ke Kataku</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
